
import React, { useState } from "react"
import { Calendar, CheckCircle, XCircle, Clock, User, MapPin } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { useAllBookings, useUpdateBookingStatus } from "@/hooks/useBookings"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

interface GerenciarAgendamentosModalProps {
  children: React.ReactNode
}

const GerenciarAgendamentosModal = ({ children }: GerenciarAgendamentosModalProps) => {
  const [open, setOpen] = useState(false)
  const { data: bookings = [], isLoading } = useAllBookings()
  const updateBookingStatus = useUpdateBookingStatus()

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800 border-green-200"
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200"
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200"
      case "cancelled":
        return "bg-gray-100 text-gray-800 border-gray-200"
      default:
        return "bg-gray-100 text-gray-800 border-gray-200"
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "confirmed":
        return "Confirmado"
      case "pending":
        return "Pendente"
      case "rejected":
        return "Rejeitado"
      case "cancelled":
        return "Cancelado"
      default:
        return status
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  const pendingBookings = bookings.filter(b => b.status === 'pending')
  const confirmedBookings = bookings.filter(b => b.status === 'confirmed')
  const rejectedBookings = bookings.filter(b => b.status === 'rejected')

  const handleApprove = async (bookingId: string) => {
    try {
      await updateBookingStatus.mutateAsync({ bookingId, status: 'confirmed' })
    } catch (error) {
      // Error já tratado no hook
    }
  }

  const handleReject = async (bookingId: string) => {
    try {
      await updateBookingStatus.mutateAsync({ bookingId, status: 'rejected' })
    } catch (error) {
      // Error já tratado no hook
    }
  }

  const BookingCard = ({ booking, showActions = false }: { booking: any, showActions?: boolean }) => (
    <Card key={booking.id} className="mb-4">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">{booking.common_areas?.image_emoji || '🏢'}</div>
            <div>
              <CardTitle className="text-lg">{booking.area_name}</CardTitle>
              <div className="flex items-center space-x-2 text-sm text-gray-600 mt-1">
                <User className="h-4 w-4" />
                <span>{booking.user_name}</span>
              </div>
            </div>
          </div>
          <Badge variant="outline" className={getStatusColor(booking.status)}>
            {getStatusLabel(booking.status)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2 text-sm">
          <div className="flex items-center space-x-2 text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(booking.booking_date)}</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-600">
            <Clock className="h-4 w-4" />
            <span>{booking.start_time} - {booking.end_time}</span>
          </div>
          {booking.notes && (
            <div className="flex items-start space-x-2 text-gray-600">
              <MapPin className="h-4 w-4 mt-0.5" />
              <span>{booking.notes}</span>
            </div>
          )}
        </div>
        {showActions && booking.status === 'pending' && (
          <div className="flex space-x-2 pt-2">
            <Button 
              size="sm" 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleApprove(booking.id)}
              disabled={updateBookingStatus.isPending}
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              Aprovar
            </Button>
            <Button 
              size="sm" 
              variant="destructive"
              onClick={() => handleReject(booking.id)}
              disabled={updateBookingStatus.isPending}
            >
              <XCircle className="h-4 w-4 mr-1" />
              Rejeitar
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Gerenciar Agendamentos</span>
          </DialogTitle>
          <DialogDescription>
            Aprovar, rejeitar e gerenciar agendamentos das áreas comuns.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500">Carregando agendamentos...</p>
            </div>
          ) : (
            <Tabs defaultValue="pending" className="h-full flex flex-col">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="pending">
                  Pendentes ({pendingBookings.length})
                </TabsTrigger>
                <TabsTrigger value="confirmed">
                  Confirmados ({confirmedBookings.length})
                </TabsTrigger>
                <TabsTrigger value="rejected">
                  Rejeitados ({rejectedBookings.length})
                </TabsTrigger>
              </TabsList>
              
              <div className="flex-1 overflow-y-auto mt-4">
                <TabsContent value="pending" className="mt-0">
                  {pendingBookings.length > 0 ? (
                    pendingBookings.map(booking => (
                      <BookingCard key={booking.id} booking={booking} showActions={true} />
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-8">
                      Nenhum agendamento pendente.
                    </p>
                  )}
                </TabsContent>
                
                <TabsContent value="confirmed" className="mt-0">
                  {confirmedBookings.length > 0 ? (
                    confirmedBookings.map(booking => (
                      <BookingCard key={booking.id} booking={booking} />
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-8">
                      Nenhum agendamento confirmado.
                    </p>
                  )}
                </TabsContent>
                
                <TabsContent value="rejected" className="mt-0">
                  {rejectedBookings.length > 0 ? (
                    rejectedBookings.map(booking => (
                      <BookingCard key={booking.id} booking={booking} />
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-8">
                      Nenhum agendamento rejeitado.
                    </p>
                  )}
                </TabsContent>
              </div>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default GerenciarAgendamentosModal
